import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useRegistryPerms } from '../lib/perms'
import { Upload, Loader2, Check, MapPin } from 'lucide-react'
import Drawer from './Drawer'
import MapBoundsEditor from './MapBoundsEditor'
import { useWS } from '../contexts/WSContext'
import { apiError } from '../lib/api'

// Wizard de aerolevantamento: (1) upload do GeoTIFF + metadados, (2) ajuste dos
// limites (bounds) sobre o Google Maps. Com editMap, abre direto na etapa 2.
// O chamador passa uma `key` por levantamento: o estado inicial (inclusive o passo)
// é derivado de editMap e sem remontar o "Ajustar limites" abria na área de upload.
export default function MapUploadWizard({ open, onClose, editMap = null }) {
  const { t } = useTranslation()
  // Segunda barreira: mesmo que alguém chegue aqui (o gate visual do MapsAdminView é
  // cosmético), não deixa nem escolher o arquivo. Sem isto, centenas de MB subiriam
  // antes do 403.
  const { mayEdit } = useRegistryPerms()
  const { maps, mines, reloadMaps } = useWS()
  const [step, setStep] = useState(editMap ? 2 : 1)
  const [file, setFile] = useState(null)
  const [name, setName] = useState(editMap?.name || '')
  const [mineId, setMineId] = useState(editMap?.mine_id ? String(editMap.mine_id) : '')
  const [date, setDate] = useState(editMap?.survey_date || '')
  const [createdId, setCreatedId] = useState(editMap?.id || null)
  const [bounds, setBounds] = useState(editMap?.bounds || null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)

  const targetId = createdId
  const liveMap = maps.find(m => m.id === targetId) || editMap || null
  const mine = mines.find(m => String(m.id) === String(mineId))

  const pickFile = (e) => {
    e.preventDefault()
    const f = e.dataTransfer?.files?.[0] || e.target.files?.[0]
    if (f) {
      setFile(f)
      if (!name) setName(f.name.replace(/\.(tif|tiff)$/i, ''))
    }
  }

  const upload = async () => {
    if (!file) { setError(t('admin:maps.wizard.select_file')); return }
    setBusy(true); setError(null)
    try {
      const fd = new FormData()
      fd.append('file', file)
      fd.append('name', name.trim() || file.name)
      if (mineId) fd.append('mine_id', mineId)
      if (date) fd.append('survey_date', date)
      const res = await fetch('/api/maps', { method: 'POST', body: fd })
      if (!res.ok) throw await apiError(res)
      const m = await res.json()
      setCreatedId(m.id)
      setStep(2)
    } catch (e) { setError(e.message) } finally { setBusy(false) }
  }

  const saveBounds = async (close) => {
    if (!targetId) { setError(t('admin:maps.wizard.no_target')); return }
    setBusy(true); setError(null)
    try {
      const body = { active: true }
      if (bounds) body.bounds = bounds
      const res = await fetch(`/api/maps/${targetId}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      })
      if (!res.ok) throw await apiError(res)
      reloadMaps()
      if (close) reset()
    } catch (e) { setError(e.message) } finally { setBusy(false) }
  }

  const reset = () => {
    setStep(editMap ? 2 : 1); setFile(null); setName(''); setMineId(''); setDate('')
    setCreatedId(null); setBounds(null); setError(null); onClose()
  }

  const statusBadge = () => {
    if (!liveMap) return null
    if (liveMap.status === 'processing')
      return <span className="inline-flex items-center gap-1.5 text-xs text-status-stop"><Loader2 size={13} className="animate-spin" /> {t('admin:maps.processing', { progress: liveMap.progress || 0 })}</span>
    if (liveMap.status === 'failed')
      return <span className="text-xs text-status-down">{t('admin:maps.wizard.failed_with', { error: liveMap.error || t('admin:maps.wizard.error_generic') })}</span>
    return <span className="inline-flex items-center gap-1.5 text-xs text-status-op"><Check size={13} /> {t('admin:maps.processed')}</span>
  }

  return (
    <Drawer
      open={open}
      onClose={reset}
      title={editMap ? t('admin:maps.wizard.edit_title') : t('admin:maps.add')}
      subtitle={step === 1 ? t('admin:maps.wizard.step1_subtitle') : t('admin:maps.wizard.step2_subtitle')}
      width="w-[34rem]"
      footer={
        <div className="flex items-center justify-between gap-2">
          <div>{statusBadge()}</div>
          <div className="flex gap-2">
            <button type="button" onClick={reset} className="text-sm px-3 py-1.5 rounded-md text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800">
              {step === 2 ? t('common:actions.close') : t('common:actions.cancel')}
            </button>
            {step === 1 ? (
              <button type="button" onClick={upload} disabled={busy || !file || !mayEdit}
                className="text-sm font-semibold px-4 py-1.5 rounded-md bg-brand text-white hover:bg-brand-700 disabled:bg-slate-200 disabled:text-slate-400">
                {busy ? t('admin:maps.wizard.uploading') : t('admin:maps.wizard.upload')}
              </button>
            ) : (
              <button type="button" onClick={() => saveBounds(true)} disabled={busy}
                className="text-sm font-semibold px-4 py-1.5 rounded-md bg-brand text-white hover:bg-brand-700 disabled:bg-slate-200 disabled:text-slate-400">
                {busy ? t('admin:actions.saving') : t('admin:maps.wizard.save_bounds')}
              </button>
            )}
          </div>
        </div>
      }
    >
      <div className="px-5 py-4 space-y-4">
        {step === 1 && (
          <>
            <div>
              <span className="text-xs font-medium text-slate-600 dark:text-slate-300">{t('admin:maps.wizard.file_label')}</span>
              <label onDrop={pickFile} onDragOver={e => e.preventDefault()}
                className="mt-1 flex flex-col items-center justify-center gap-1 border-2 border-dashed border-slate-200 dark:border-slate-700 rounded-lg px-3 py-8 text-center cursor-pointer hover:border-brand/50">
                <Upload size={20} className="text-slate-400 dark:text-slate-500" />
                {file
                  ? <span className="text-xs text-slate-600 dark:text-slate-300">{file.name} <span className="text-slate-400">· {t('admin:maps.wizard.file_size', { size: (file.size / 1e6).toFixed(0) })}</span></span>
                  : <span className="text-xs text-slate-400 dark:text-slate-500">{t('admin:maps.wizard.drop_hint')}</span>}
                <input type="file" accept=".tif,.tiff,image/tiff" disabled={!mayEdit} className="hidden" onChange={pickFile} />
              </label>
              <p className="mt-1 text-[11px] text-slate-400 dark:text-slate-500">{t('admin:maps.wizard.process_help')}</p>
            </div>
            <label className="block"><span className="text-xs font-medium text-slate-600 dark:text-slate-300">{t('admin:maps.wizard.name')}</span>
              <input value={name} onChange={e => setName(e.target.value)} placeholder={t('admin:maps.wizard.name_ph')} className="mt-1 w-full text-sm border border-slate-200 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand/30 dark:bg-slate-900 dark:border-slate-700 dark:text-slate-100 dark:placeholder-slate-500" /></label>
            <div className="grid grid-cols-2 gap-3">
              <label className="block"><span className="text-xs font-medium text-slate-600 dark:text-slate-300">{t('common:entities.mine')}</span>
                <select value={mineId} onChange={e => setMineId(e.target.value)} className="mt-1 w-full text-sm border border-slate-200 rounded-md px-3 py-2 bg-white dark:bg-slate-900 dark:border-slate-700 dark:text-slate-100">
                  <option value="">—</option>
                  {mines.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                </select></label>
              <label className="block"><span className="text-xs font-medium text-slate-600 dark:text-slate-300">{t('admin:maps.wizard.survey_date')}</span>
                <input type="date" value={date} onChange={e => setDate(e.target.value)} className="mt-1 w-full text-sm border border-slate-200 rounded-md px-3 py-2 bg-white dark:bg-slate-900 dark:border-slate-700 dark:text-slate-100" /></label>
            </div>
          </>
        )}

        {step === 2 && (
          <>
            <div className="flex items-center justify-between">
              <p className="text-xs text-slate-500 dark:text-slate-400 flex items-center gap-1.5"><MapPin size={13} /> {t('admin:maps.wizard.bounds_help')}</p>
            </div>
            {liveMap?.status === 'processing' && (
              <div className="h-1.5 w-full rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden">
                <div className="h-full bg-brand transition-all" style={{ width: `${liveMap.progress || 5}%` }} />
              </div>
            )}
            <MapBoundsEditor mapObj={liveMap} mine={mine} onChange={setBounds} />
            <p className="text-[11px] text-slate-400 dark:text-slate-500">
              {t('admin:maps.wizard.bounds_note')}
            </p>
          </>
        )}

        {error && <p className="text-xs text-red-600">{error}</p>}
      </div>
    </Drawer>
  )
}
